import {useEffect, useState} from 'react';
import {animate, dynamic, isServer, toIntent} from '@sail/engine';
import {tokens as $} from '@sail/tokens';
import {css} from '../css';

const visualViewport = !isServer ? window.visualViewport : undefined;

type ViewportSize = {
  width: number;
  height: number;
};

function getViewportSize(): ViewportSize {
  return {
    // eslint-disable-next-line @sail/ui-no-reflow
    width: visualViewport?.width || window.innerWidth,
    // eslint-disable-next-line @sail/ui-no-reflow
    height: visualViewport?.height || window.innerHeight,
  };
}

// Use visualViewport api to track viewport available height when iOS virtual keyboard is open
function useViewportArea(): ViewportSize {
  const [size, setSize] = useState(() =>
    isServer ? {width: 0, height: 0} : getViewportSize(),
  );

  useEffect(() => {
    const onResize = () => {
      setSize(getViewportSize());
    };

    visualViewport?.addEventListener('resize', onResize);
    visualViewport?.addEventListener('scroll', onResize);

    return () => {
      visualViewport?.removeEventListener('resize', onResize);
      visualViewport?.removeEventListener('scroll', onResize);
    };
  }, []);

  return size;
}

export type MobileDisplay = 'bottom-sheet' | 'fullscreen-sheet';

export const bottomSheetStyles = dynamic(() => {
  const {height} = useViewportArea();

  const keyboardHeight =
    // If we are in an iframe (Connect) we set this as 0px
    window.parent === window
      ? `max(var(--viewport-height) - ${height}px, env(safe-area-inset-bottom), 0px)`
      : '0px';

  return [
    css({
      '--viewport-height': '100vh',
      '@supports (height: 100dvh)': {
        '--viewport-height': '100dvh',
      },
      '--keyboard-height': keyboardHeight,
      position: 'fixed',
      bottom: 'space.0',
      top: 'initial',
      right: 'initial',
      left: 'initial',
      width: '100vw',
      borderWidth: '0px',
      borderBottomLeftRadius: '0px',
      borderBottomRightRadius: '0px',
      borderTopLeftRadius: $['space.150'],
      borderTopRightRadius: $['space.150'],
      minHeight:
        'max(calc(var(--viewport-height) * 0.25), var(--close-confirmation-dialog-height, 0px))',
      maxHeight:
        'calc(var(--viewport-height) - env(safe-area-inset-bottom, 0) - env(safe-area-inset-top, 0) - 40px)',
      boxShadow: 'overlay.inverted',
      transform: 'translateY(0%)',
      transition: 'padding-bottom 100ms ease-out',
      paddingBottom: 'var(--keyboard-height)',
      backgroundColor: 'surface',
    }),
  ];
});

export const fullscreenSheetStyles = css({
  width: 'fill',
  minHeight: 'var(--close-confirmation-dialog-height, 0px)',
  height:
    'calc(100% - var(--sail-layer-margin-top, 0px) - var(--sail-layer-margin-bottom, 0px))',
  borderRadius: '0px',
  maxHeight: 'fill',
  borderWidth: '0px',
  top: 'var(--sail-layer-margin-top, 0px)',
  boxShadow: 'none',
});

export const fullscreenAnimation = (disabled: boolean) =>
  animate.inout({
    duration: 250,
    opacity: 0,
    transform: 'scale(0.95, 0.95)',
    easing: 'ease-in-out',
    disabled,
  });

export const bottomSheetAnimation = (disabled: boolean) =>
  toIntent([
    animate.inout({
      duration: 250,
      transform: 'translateY(100%)',
      easing: 'ease-out',
      disabled,
    }),
  ]);
